<template>
	<div v-if="Object.keys(cache).length">
		<div :class="{ 'p4 bg5 r2 bc7 b1': isBoxed }">
			<!-- Render schema -->
			<div
				v-for="(field, i) in computedSchema"
				:key="i"
				:class="[
					`mb${gap}`,
					{ 'b2 bcWarning p2 r2': $store.state.caseManagement.isGodMode }
				]"
			>
				<form-field
					:name="`${Math.random()}`"
					:componentName="field.component"
					:label="field.label"
					:caseId="$route.params.id"
					:proposalId="$store.state.caseManagement.proposalId"
					:offerId="$store.state.caseManagement.offerId"
					:pricing="cache.premium ? cache.premium : {}"
					:productId="cache.product ? cache.product.productId : null"
					v-bind="field.props"
					v-model="form[field.model]"
					:size="size"
					@blur="updateField(field)"
					@updated="updateField(field)"
					@premium:updated="fetchOffer"
					@premium:added="fetchOffer"
					@discount:updated="fetchOffer"
					@discount:added="fetchOffer"
					@discount:deleted="fetchOffer"
					@loadings:updated="fetchOffer"
					@loadings:added="fetchOffer"
					@loadings:deleted="fetchOffer"
				/>

				<!-- ########################################## -->
				<!-- God mode will allow to see data bindings + special buttons for liquid generation -->
				<!-- ########################################## -->
				<template v-if="$store.state.caseManagement.isGodMode">
					<div
						class="xs-12 bg7--l3 p4 r2 mt1"
						v-if="field.component !== 'html-box'"
					>
						<div class="lh125">
							<span class="fw7">data binding:</span> {{ field.bindTo }}
						</div>
						<div class="lh125 mt2">
							<j-button
								@click="handleLiquidGeneration(field, 'cases')"
								size="xsmall"
								type="bordered"
							>
								Get Offer Liquid
							</j-button>
							<j-button
								class="ml4"
								@click="handleLiquidGeneration(field, 'policies')"
								size="xsmall"
								type="bordered"
							>
								Get Policy Liquid
							</j-button>
						</div>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import { computeSchema } from "../utils/computeSchema.js"
import { generateLiquid } from "../utils/liquidGenerator.js"

/**
 * GraphQL queries
 */
const UPDATE_OFFER_OF_PROPOSAL = gql`
	mutation updateOfferOfProposal(
		$caseId: String!
		$proposalId: String!
		$offerId: String!
		$input: updateOfferInput!
	) {
		updateOfferOfProposal(
			caseId: $caseId
			proposalId: $proposalId
			offerId: $offerId
			updateOfferInput: $input
		) {
			errors
			status
		}
	}
`

const ADD_FACT_OF_PROPOSAL_OFFER = gql`
	mutation addFactToProposalOffer(
		$caseId: String!
		$proposalId: String!
		$offerId: String!
		$input: addFactInput!
	) {
		addFactToProposalOffer(
			caseId: $caseId
			proposalId: $proposalId
			offerId: $offerId
			input: $input
		) {
			status
			errors
			createdStatus {
				id
			}
		}
	}
`

const UPDATE_FACT_OF_PROPOSAL_OFFER = gql`
	mutation updateFactOfProposalOffer(
		$caseId: String!
		$proposalId: String!
		$offerId: String!
		$factId: String!
		$input: updateFactInput!
	) {
		updateFactOfProposalOffer(
			caseId: $caseId
			proposalId: $proposalId
			offerId: $offerId
			factId: $factId
			input: $input
		) {
			status
			errors
		}
	}
`

export default {
	components: {
		// NOTE: we have to register this component async as we run into circular reference issues
		// see more: https://vuejs.org/v2/guide/components-edge-cases.html#Circular-References-Between-Components
		FormField: () => import("./FormField/FormField.vue"),
		JButton
	},

	props: {
		/**
		 * OfferId
		 */
		offerId: {
			type: String
		},

		/**
		 * Schema for the form
		 */
		schema: {
			type: Array,
			required: true,
			default: () => []
		},

		/**
		 * Size of the form inputs
		 */
		size: {
			validator(val) {
				return ["xsmall", "small", "medium", "large"].includes(val)
			},
			default: "medium"
		},

		/**
		 * Vertical gap between fields
		 * It translates into mb{gap} class
		 */
		gap: {
			type: String,
			default: "6"
		},

		/**
		 * If this is true, entity will have a class that creates a visual box around the
		 * whole entity form
		 */
		isBoxed: {
			type: Boolean,
			default: true
		}
	},

	data() {
		return {
			cache: {},
			form: {}
		}
	},

	computed: {
		offerId_() {
			return this.offerId
				? this.offerId
				: this.$store.state.caseManagement.offerId
		},

		/**
		 * Schema with proper component names, added model
		 */
		computedSchema() {
			return computeSchema(this.schema)
		}
	},

	methods: {
		async fetchOffer() {
			if (!this.offerId_) this.form = {}

			const query = gql`
				query offer($where: caseWhere) {
					cases(where: $where) {
						list {
							proposals {
								id
								basket {
									id
									status
									startDate
									endDate
									policyNumber
									facts {
										id
										type
										value {
											booleanValue
											dateValue
											numberValue
											stringValue
										}
									}
									premium {
										amount
										currencyCode
										formattedPrice
										originalPrice
										formattedOriginalPrice
										appliedTaxes {
											code
											name
											ratio
											flat
											formattedFlat
											order
											formattedOriginalPrice
											originalPrice
										}
										appliedDiscounts {
											id
											name
											code
											ratio
											flat
											formattedFlat
											order
											formattedOriginalPrice
											originalPrice
										}
										loadings {
											id
											codeName {
												code
												name
											}
											ratio
											flat
										}
									}
									product {
										name
										insurer {
											name
										}
										productId {
											plan
											type
											version
										}
									}
								}
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: { where: { id: this.$route.params.id } }
			})

			this.cache = {
				...res?.data?.cases?.list?.[0]?.proposals
					?.find(
						proposal =>
							proposal.id === this.$store.state.caseManagement.proposalId
					)
					?.basket?.find(
						offer => offer.id === this.$store.state.caseManagement.offerId
					)
			}

			this.populateFormFromCache()

			this.$store.commit("caseManagement/publish", {
				eventName: "offer:updated",
				data: null
			})
		},

		populateFormFromCache() {
			this.computedSchema.forEach(field => {
				// Facts
				if (field.bindTo.includes("offer.facts")) {
					this.$set(
						this.form,
						field.model,
						this.cache?.facts?.find(item => item.type === field.model)?.value?.[
							`${field.dataType || "string"}Value`
						]
					)
					return
				}

				// Product
				if (field.bindTo === "offer.product") {
					let output = []

					// If there is a product
					if (this.cache?.product?.productId)
						output.push({
							name: this.cache?.product?.name,
							value: this.cache?.product?.productId
						})

					this.$set(this.form, "product", output)
					return
				}

				// Rest
				this.$set(this.form, field.model, this.cache?.[field.model])
			})
		},

		async updateField(field) {
			// Product
			if (field.bindTo === "offer.product")
				return await this.updateProductHandler(field)

			// Facts
			if (field.bindTo.includes("offer.facts"))
				return await this.upsertFactHandler(field)

			// Others
			await this.updateOfferHandler(field)
		},

		async updateOfferHandler(field) {
			const query = UPDATE_OFFER_OF_PROPOSAL
			const res = await api({
				query,
				variables: {
					caseId: this.$route.params.id,
					proposalId: this.$store.state.caseManagement.proposalId,
					offerId: this.$store.state.caseManagement.offerId,
					input: {
						[field.model]: this.form[field.model]
					}
				}
			})

			// Check errors
			if (res?.data?.updateOfferOfProposal?.status !== "success") {
				this.showError(field.label)
				this.populateFormFromCache()
				return Promise.resolve()
			}

			return Promise.resolve()
		},

		async updateProductHandler(field) {
			const query = UPDATE_OFFER_OF_PROPOSAL
			const res = await api({
				query,
				variables: {
					caseId: this.$route.params.id,
					proposalId: this.$store.state.caseManagement.proposalId,
					offerId: this.$store.state.caseManagement.offerId,
					input: {
						productId: this.form?.[field.model]?.[0]?.productId
					}
				}
			})

			// Check errors
			if (res?.data?.updateOfferOfProposal?.status !== "success") {
				this.showError(field.label)
				this.populateFormFromCache()
				return Promise.resolve()
			}

			// Update cache
			this.$set(this.cache, "product", this.form[field.model]?.[0])

			return Promise.resolve()
		},

		async upsertFactHandler(field) {
			let cachedData = this?.cache?.facts?.find(
				item => item.type === field.model
			)

			// Create
			if (!cachedData?.id) {
				const { caseId, proposalId, offerId } = this.$store.state.caseManagement
				const input = {
					type: field.model,
					value: {
						[`${field?.dataType || "string"}Value`]: this.form[field.model]
					}
				}
				const variables = {
					caseId,
					proposalId,
					offerId,
					input
				}
				const res = await api({ query: ADD_FACT_OF_PROPOSAL_OFFER, variables })

				// Errors
				if (res?.data?.addFactToProposalOffer?.status !== "success") {
					this.showError(field.label)
					this.populateFormFromCache()
					return
				}

				// Update cache
				this.cache?.facts?.push({
					id: res?.data?.addFactToProposalOffer?.createdStatus?.id,
					...input
				})

				return Promise.resolve()
			}

			// Update
			// Check if value is different from cache first, if it is not, don't run
			if (
				cachedData?.value?.[`${field.dataType || "string"}Value`] ===
				this.form[field.model]
			)
				return

			const { caseId, proposalId, offerId } = this.$store.state.caseManagement
			const variables = {
				caseId,
				proposalId,
				offerId,
				factId: cachedData?.id,
				input: {
					type: field.model,
					value: {
						[`${field?.dataType || "string"}Value`]: this.form[field.model]
					}
				}
			}
			const res = await api({ query: UPDATE_FACT_OF_PROPOSAL_OFFER, variables })

			// Errors
			if (res?.data?.updateFactOfProposalOffer?.status !== "success") {
				this.showError(field.label)
				this.populateFormFromCache()
				return Promise.resolve()
			}

			// Update cache
			cachedData.value = {
				[`${field?.dataType || "string"}Value`]: this.form[field.model]
			}

			return Promise.resolve()
		},

		async apiAddOfferFact(variables) {
			const query = gql`
				mutation addFactToProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: addFactInput!
				) {
					addFactToProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						input: $input
					) {
						status
						errors
						createdStatus {
							id
						}
					}
				}
			`

			const res = await api({
				query,
				variables
			})

			return Promise.resolve(res)
		},

		async apiUpdateOfferFact(offerId, input) {
			const query = gql`
				mutation updateFactOfProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$factId: String!
					$input: updateFactInput!
				) {
					updateFactOfProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						factId: $factId
						updateFactInput: $input
					) {
						status
						errors
					}
				}
			`
			const res = await api({
				query,
				variables: {
					entityId,
					input
				}
			})

			return Promise.resolve(res)
		},

		showError(fieldName) {
			this.$store.dispatch("showError", [
				`Field ${fieldName} wasn't saved, please update it again`
			])
		},

		/**
		 * Helper method to generate liquid syntax based on field
		 */
		handleLiquidGeneration(field, forWhat) {
			// Helper to save value to clipboard
			//  Not guaranteed to work everywhere, works in Chrome
			const copyToClipboard = str => {
				const el = document.createElement("textarea")
				el.value = str
				el.setAttribute("readonly", "")
				el.style.position = "absolute"
				el.style.left = "-9999px"
				document.body.appendChild(el)
				el.select()
				document.execCommand("copy")
				document.body.removeChild(el)
			}

			// Generate liquid
			const generatedLiquid = generateLiquid(field, forWhat)
			console.log({ field })
			console.log(generatedLiquid)

			// Save result to clipboard
			copyToClipboard(generatedLiquid)

			// Show message
			this.$store.dispatch("addMessage", {
				delay: 1000,
				message: {
					type: "neutral",
					text: "Copied to your clipboard!"
				}
			})
		}
	},

	watch: {
		offerId_: {
			handler() {
				this.fetchOffer()
			},
			immediate: true
		}
	}
}
</script>
